import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChartMetric } from '../App';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: (number | null)[];
    backgroundColor: string;
    borderRadius?: number;
    barPercentage?: number;
    categoryPercentage?: number;
  }[];
}
interface CampaignOverviewChartProps {
  data: ChartData;
  chartMetric: ChartMetric;
  setChartMetric: (metric: ChartMetric) => void;
}
const metricLabels: Record<ChartMetric, string> = {
  budget: 'Budget',
  leads: 'Leadi',
  cpc: 'Cost per Click'
};
const formatValue = (value: number, metric: ChartMetric): string => {
  if (metric === 'cpc') {
    return `€${value.toFixed(2)}`;
  }
  if (metric === 'budget') {
    if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `€${(value / 1000).toFixed(0)}K`;
    return `€${value}`;
  }
  // leads
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};
export function CampaignOverviewChart({
  data,
  chartMetric,
  setChartMetric
}: CampaignOverviewChartProps) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    skipNull: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleFont: {
          size: 13,
          family: 'system-ui, -apple-system, sans-serif'
        },
        bodyFont: {
          size: 13,
          family: 'system-ui, -apple-system, sans-serif'
        },
        padding: 12,
        cornerRadius: 8,
        filter: function (tooltipItem: any) {
          return tooltipItem.raw !== null;
        },
        callbacks: {
          title: function (items: any[]) {
            if (!items.length) return '';
            return items[0].label;
          },
          label: function (context: any) {
            const value = context.raw;
            if (value === null) return '';
            return ` ${context.dataset.label}: ${formatValue(value, chartMetric)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: 'system-ui, -apple-system, sans-serif',
            size: 13,
            weight: '600' as const
          }
        }
      },
      y: {
        border: {
          display: false
        },
        grid: {
          color: '#f1f5f9'
        },
        ticks: {
          font: {
            family: 'system-ui, -apple-system, sans-serif'
          },
          callback: function (value: any) {
            return formatValue(value, chartMetric);
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      intersect: true
    },
    borderRadius: 6
  };
  const metrics: ChartMetric[] = ['budget', 'leads', 'cpc'];
  return <div className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-id="element-50">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-51">
        <div data-id="element-52">
          <h3 className="text-lg font-semibold text-foreground" data-id="element-53">
            Primerjava kampanj
          </h3>
          <p className="text-sm text-muted-foreground mt-1" data-id="element-54">
            {metricLabels[chartMetric]} po kampanjah, razvrščeno po letih
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-55">
          {/* Metric toggle buttons */}
          <div className="flex bg-muted rounded-lg p-0.5" data-id="element-56">
            {metrics.map(metric => <button key={metric} onClick={() => setChartMetric(metric)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${chartMetric === metric ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-57">
                {metricLabels[metric]}
              </button>)}
          </div>
          {/* Type legend */}
          <div className="hidden sm:flex items-center gap-3 ml-3 pl-3 border-l border-border text-xs" data-id="element-58">
            <div className="flex items-center gap-1.5" data-id="element-59">
              <span className="w-2.5 h-2.5 rounded-sm bg-slate-800 inline-block" data-id="element-60"></span>
              <span className="text-muted-foreground" data-id="element-61">Brand</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-62">
              <span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" data-id="element-63"></span>
              <span className="text-muted-foreground" data-id="element-64">Tactical</span>
            </div>
          </div>
        </div>
      </div>
      <div className="h-[380px] w-full" data-id="element-65">
        {data.datasets.length > 0 ? <Bar options={options} data={data as any} data-id="element-66" /> : <div className="h-full flex items-center justify-center text-muted-foreground" data-id="element-67">
            Ni podatkov za izbrane filtre
          </div>}
      </div>
    </div>;
}